import type { ProfileData } from '@/lib/profile/types';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'ahoy-cache-user-profile-';
const COMPONENT = 'persisted-user-profile';

function isProfileDataShape(raw: unknown): raw is ProfileData {
  if (typeof raw !== 'object' || raw === null) {
    return false;
  }
  const obj = raw as Record<string, unknown>;
  if (typeof obj.user !== 'object' || obj.user === null) {
    return false;
  }
  const user = obj.user as Record<string, unknown>;
  return typeof user.id === 'number';
}

export function getPersistedUserProfile(
  userId: number | undefined
): ProfileData | undefined {
  if (userId === undefined) {
    return undefined;
  }
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  return getPersistedCache<ProfileData>(
    key,
    CACHE_VERSION,
    isProfileDataShape,
    COMPONENT
  );
}

export function setPersistedUserProfile(
  userId: number,
  profile: ProfileData
): void {
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  setPersistedCache<ProfileData>(key, profile, CACHE_VERSION, COMPONENT);
}

export function clearAllPersistedUserProfile(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}

/**
 * Best-effort synchronous read of the persisted profile for pre-React callers
 * (the dashboard prefetch) that cannot use hooks. Returns the profile ONLY
 * when exactly one profile is persisted (the common single-user-device case);
 * otherwise undefined. Never throws.
 *
 * NOTE: unlike useUserProfileData (which keys hydration on the authenticated
 * user id), no auth id is available before React mounts, so this uses a
 * single-profile heuristic. On a shared device with multiple persisted
 * profiles it returns undefined; with a single stale profile it may return the
 * previous user's profile. Callers use it ONLY to build prefetch cache keys
 * and gate hydration (never to render), and everything self-heals once the
 * hook resolves the live profile.
 */
export function getSinglePersistedUserProfile(): ProfileData | undefined {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    if (keys.length !== 1) {
      return undefined;
    }
    const userId = Number(keys[0].slice(STORAGE_KEY_PREFIX.length));
    if (!Number.isFinite(userId)) {
      return undefined;
    }
    return getPersistedUserProfile(userId);
  } catch {
    return undefined;
  }
}

/**
 * The persisted display timezone, or null when it is not known — see
 * getSinglePersistedUserProfile for when that is. Callers that need a zone to
 * RENDER fall back to LOADING_FALLBACK_TIMEZONE; callers that need a zone to
 * FETCH day-scoped data (the dashboard prefetch) treat null as "let the server
 * derive the day from the profile" rather than guessing.
 */
export function getPersistedDisplayTimezone(): string | null {
  return getSinglePersistedUserProfile()?.user?.timezone || null;
}
